import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Edit, Trash2, Eye } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useState } from 'react'
import type { Location } from '@/types/entities'

interface LocationActionsProps {
    location: Location
    /** Absent for a role without locations.update: the entry is not offered. */
    onEdit?: (location: Location) => void
    /** Absent for a role without locations.delete. */
    onDelete?: (location: Location) => void
    onView: (location: Location) => void
}

function LocationActions({ location, onEdit, onDelete, onView }: LocationActionsProps) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)

    return (
        <div
            className="flex justify-center"
            onClick={(e) => {
                e.stopPropagation()
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
            onMouseUp={(e) => {
                e.stopPropagation()
            }}
            onPointerDown={(e) => {
                e.stopPropagation()
            }}
            onPointerUp={(e) => {
                e.stopPropagation()
            }}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setOpen(true)
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                        onMouseUp={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                    >
                        <span className="sr-only">{t('locations.openMenu')}</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    side="bottom"
                    sideOffset={5}
                    className="w-56"
                    onCloseAutoFocus={(e) => {
                        e.preventDefault()
                    }}
                >
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onView(location)
                            setOpen(false)
                        }}
                    >
                        <Eye className="mr-2 h-4 w-4" />
                        {t('locations.viewLocation')}
                    </DropdownMenuItem>
                    {onEdit && (
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation()
                                onEdit(location)
                                setOpen(false)
                            }}
                        >
                            <Edit className="mr-2 h-4 w-4" />
                            {t('locations.editLocation')}
                        </DropdownMenuItem>
                    )}
                    {onDelete && (
                        <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => {
                                e.stopPropagation()
                                onDelete(location)
                                setOpen(false)
                            }}
                        >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t('locations.deleteLocation')}
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}

export function createLocationColumns(
    onDelete: ((location: Location) => void) | undefined,
    onEdit: ((location: Location) => void) | undefined,
    onView: (location: Location) => void,
    t: (key: string) => string
): ColumnDef<Location>[] {
    return [
        {
            accessorKey: 'description',
            header: t('locations.description'),
            size: 200,
            cell: ({ row }) => {
                const description = row.getValue('description') as string
                return (
                    <div className="font-medium text-sm">
                        {description || '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'category',
            header: t('locations.category'),
            size: 180,
            cell: ({ row }) => {
                const category = row.getValue('category') as string
                return (
                    <div className="text-sm text-muted-foreground">
                        {category || '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'city',
            header: t('locations.city'),
            size: 150,
            cell: ({ row }) => {
                const city = row.getValue('city') as string
                // A country or a region has no city: say which it is instead of a blank.
                const scope = row.original.scope ?? 'city'
                if (scope !== 'city') {
                    return (
                        <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground" data-testid="location-kind-badge">
                            {scope === 'country' ? t('locations.kindCountry') : t('locations.kindRegion')}
                        </span>
                    )
                }
                return (
                    <div className="text-sm text-muted-foreground">
                        {city || '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'country',
            header: t('locations.country'),
            size: 150,
            cell: ({ row }) => {
                const country = row.getValue('country') as string
                return (
                    <div className="text-sm text-muted-foreground">
                        {country || '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'noofObjects',
            header: () => (
                <div className="text-center w-full">
                    {t('locations.numberOfObjects')}
                </div>
            ),
            size: 150,
            cell: ({ row }) => {
                const noofObjects = row.getValue('noofObjects') as number
                return (
                    <div className="text-center font-medium text-sm">
                        {noofObjects !== null && noofObjects !== undefined ? noofObjects : 0}
                    </div>
                )
            },
        },
        {
            id: 'actions',
            size: 80,
            header: () => (
                <div className="text-center w-full">
                    {t('common.actions')}
                </div>
            ),
            cell: ({ row }) => {
                const location = row.original
                return (
                    <LocationActions
                        location={location}
                        onDelete={onDelete}
                        onEdit={onEdit}
                        onView={onView}
                    />
                )
            },
        },
    ]
}

